/**
 * API Key Management API
 *
 * Functions for managing API keys for automated document uploads.
 * Admin-only endpoints.
 */

import api from './index';

/**
 * Get all API keys for the company
 *
 * @returns {Promise<Object>} Response with keys array and available permissions
 */
export const getApiKeys = () => {
  return api.get('/api-keys');
};

/**
 * Get API key usage statistics
 *
 * @returns {Promise<Object>} Response with usage stats
 */
export const getApiKeyStats = () => {
  return api.get('/api-keys/stats');
};

/**
 * Get a single API key by ID
 *
 * @param {string} keyId - API key ID
 * @returns {Promise<Object>} Response with API key details
 */
export const getApiKey = (keyId) => {
  return api.get(`/api-keys/${keyId}`);
};

/**
 * Create a new API key
 *
 * @param {Object} data - Key data
 * @param {string} data.name - Friendly name for the key
 * @param {string[]} data.permissions - Array of permission strings
 * @param {Date} data.expiresAt - Optional expiration date
 *
 * @returns {Promise<Object>} Response with apiKey and plainKey
 * @note The plainKey is only returned once - save it securely!
 */
export const createApiKey = (data) => {
  return api.post('/api-keys', data);
};

/**
 * Update API key permissions
 *
 * @param {string} keyId - API key ID
 * @param {string[]} permissions - New permissions array
 * @returns {Promise<Object>} Response with updated key
 */
export const updateApiKeyPermissions = (keyId, permissions) => {
  return api.put(`/api-keys/${keyId}/permissions`, { permissions });
};

/**
 * Revoke (deactivate) an API key
 *
 * @param {string} keyId - API key ID
 * @returns {Promise<Object>} Response with revoked key
 */
export const revokeApiKey = (keyId) => {
  return api.post(`/api-keys/${keyId}/revoke`);
};

/**
 * Permanently delete an API key
 *
 * @param {string} keyId - API key ID
 * @returns {Promise<Object>} Response with success message
 */
export const deleteApiKey = (keyId) => {
  return api.delete(`/api-keys/${keyId}`);
};
